/** The bar that replaces the rail on an iPad and a phone, and the summary sheet it opens (moved out of `pages/quote-builder-v2.tsx` at step 8). */
import { type ReactNode } from 'react'
import { BILLING_TAB, usd, type QuoteTree } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { Button } from '../components/primitives.js'
import { PanelSheet } from '../components/panel-sheet.js'
import { formatDollars } from '../lib/format.js'
import { IconCheck, IconChevronUp, IconEye, IconLock, IconWarn } from '../frame/icons.js'
import {
  ALL_CLEAR, BAR_BASE_MARGIN, BAR_VISIT_MARGIN, BASE_PRICE, MARGIN_TITLE, PREVIEW, QUOTE_SUMMARY, SUMMARY, TO_CHECK, WITH_OPTIONS,
} from '../builder/copy.js'

// ---------------------------------------------------------------------------
// What replaces the rail when there is no room for it
// ---------------------------------------------------------------------------

export function StickyTotals({
  quote, phone, primary, onPreview, open, onOpen, summary, billing, visit,
}: {
  quote: QuoteTree
  phone: boolean
  /**
   * The green button (`docs/job-stages.md` §1): `jobActions()`'s primary for
   * this quote — *Send to Karen* · *Approve* · *Schedule* … — or absent when
   * there is none. It used to be *Send* on every quote, in every state.
   */
  primary: { label: string; icon: ReactNode; onClick: () => void; disabled: boolean } | null
  onPreview: () => void
  open: boolean
  onOpen: (open: boolean) => void
  /** The rail's three cards, drawn in the summary sheet. */
  summary: { total: ReactNode; margin: ReactNode; checks: ReactNode }
  /**
   * On the Billing tab the bar says what the plans bill rather than the base
   * price (Board 15: `Billed over the season $2,890.00 · 8 invoices · first
   * Apr 1 · All clear`), and its Summary is that tab's rail (§8.4).
   */
  billing?: { amount: number; sub: string; cards: { cash: ReactNode; checks: ReactNode; defaults: ReactNode } }
  /**
   * The phone's service screen (qb-10 · 4): the bar speaks for the visit the
   * service is on — `$21,012` over `base · this visit · 40.1% margin` — and
   * is the whole row, with no Send beside it and no check chip in it.
   */
  visit?: { total: number; marginPct: number | null }
}) {
  const margin = quote.totals.margin.scopes.base.marginPct
  const marginText = margin === null ? '—' : `${margin.toFixed(1)}%`
  const visitMarginText = visit?.marginPct == null ? '—' : `${visit.marginPct.toFixed(1)}%`
  const toCheck = quote.checks.filter((check) => check.level === 'warn' || check.level === 'blocking').length
  const withOptions = quote.totals.base + quote.totals.options
  const preview = () => { onOpen(false); onPreview() }

  const allClear = billing && toCheck === 0 && (
    <span className={cn('inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-[6px] bg-accent-soft',
      'font-semibold text-accent-dark', phone ? 'h-[22px] px-[7px] text-[11px]' : 'h-6 px-[7px] text-[11.5px]')}>
      <IconCheck size={12} /> {ALL_CLEAR}
    </span>
  )
  const checkChip = toCheck > 0 && (
    <span className={cn('inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-[6px] bg-warn-bg',
      'font-semibold text-warn', phone ? 'h-[22px] px-[7px] text-[11px]' : 'h-6 px-[7px] text-[11.5px]')}>
      <IconWarn size={12} /> {TO_CHECK(toCheck)}
    </span>
  )

  return (
    <>
      <PanelSheet
        open={open}
        onClose={() => onOpen(false)}
        title={QUOTE_SUMMARY}
        sub={`${quote.reference}${quote.propertyName ? ` · ${quote.propertyName}` : ''}`}
        wide={!phone}
        tall={phone}
        footer={phone
          ? (
            <div className="flex w-full gap-2">
              <Button className="!h-12 flex-1 !rounded-record !text-[14px]" onClick={preview}>
                <IconEye size={16} /> {PREVIEW}
              </Button>
              {primary && (
                <Button variant="primary" className="!h-12 flex-[1.4] !rounded-record !text-[15px]"
                  disabled={primary.disabled} onClick={() => { onOpen(false); primary.onClick() }}>
                  {primary.icon}{primary.label}
                </Button>
              )}
            </div>
          )
          : (
            <div className="flex w-full gap-2">
              <Button className="!h-11 flex-1 !rounded-touch" onClick={preview}>
                <IconEye size={15} /> {PREVIEW}
              </Button>
              {primary && (
                <Button variant="primary" className="!h-11 flex-[2] !rounded-touch"
                  disabled={primary.disabled} onClick={() => { onOpen(false); primary.onClick() }}>
                  {primary.icon}{primary.label}
                </Button>
              )}
            </div>
          )}
      >
        {/*
          * One column on a phone; on an iPad two — Quote total over Before you
          * send in a 340px column, Margin beside them (qb-08 C).
          */}
        {billing
          ? (
            <div className={cn('flex gap-3.5', phone ? 'flex-col px-4 pt-3' : 'items-start px-6')}>
              <div className={cn('flex flex-col gap-3.5', !phone && 'w-[340px] shrink-0')}>
                {billing.cards.cash}
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-3.5">
                {billing.cards.checks}{billing.cards.defaults}
              </div>
            </div>
          )
          : phone
          ? <div className="flex flex-col gap-3 px-4 pt-3">{summary.total}{summary.margin}{summary.checks}</div>
          : (
            <div className="flex items-start gap-5 px-6">
              <div className="flex w-[340px] shrink-0 flex-col gap-3.5">{summary.total}{summary.checks}</div>
              <div className="min-w-0 flex-1">{summary.margin}</div>
            </div>
          )}
      </PanelSheet>

      {phone
        ? (
          /*
           * qb-09: a floating card that opens the summary, and Send beside it —
           * above the tab bar, which is 58px plus the safe band.
           */
          <div className="fixed inset-x-0 bottom-[58px] z-30 mb-safe flex items-center gap-2.5 px-4 py-2.5">
            <button
              type="button"
              onClick={() => onOpen(!open)}
              aria-label={QUOTE_SUMMARY}
              aria-expanded={open}
              className="flex min-w-0 flex-1 items-center gap-3 rounded-record border border-line bg-surface
                         py-[9px] pl-3.5 pr-3 text-left shadow-[0_6px_18px_rgba(20,20,10,0.08)]"
            >
              <span className="flex flex-col leading-[1.15]">
                <b className="tnum text-[17px] font-bold tracking-[-0.01em] text-text">
                  {formatDollars(billing ? billing.amount : visit ? visit.total : quote.totals.base)}
                </b>
                <span className="whitespace-nowrap text-[11px] text-text-muted">
                  {billing ? billing.sub : visit ? BAR_VISIT_MARGIN(visitMarginText) : BAR_BASE_MARGIN(marginText)}
                </span>
              </span>
              {!visit && checkChip}{allClear}
              <IconChevronUp size={16} className="ml-auto shrink-0 text-text-faint" />
            </button>
            {!visit && primary && (
              <Button variant="primary" disabled={primary.disabled} onClick={primary.onClick}
                data-sticky-primary=""
                className="!h-[50px] shrink-0 !rounded-record !px-[18px] !text-[15px]">
                {primary.icon}{primary.label}
              </Button>
            )}
          </div>
        )
        : (
          /* qb-08 A: the rail as a bar, 68px, the whole left side opening the summary. */
          <div className="sticky bottom-0 z-30 flex h-[68px] shrink-0 items-center gap-4 border-t border-line bg-surface px-5">
            <button
              type="button"
              onClick={() => onOpen(!open)}
              aria-label={QUOTE_SUMMARY}
              aria-expanded={open}
              className="flex min-w-0 flex-1 items-center gap-3.5 overflow-hidden text-left"
            >
              {billing && (
                <>
                  <span className="flex flex-col leading-[1.15]">
                    <span className="text-[11px] text-text-muted">{BILLING_TAB.barLabel}</span>
                    <b className="tnum text-[20px] font-bold tracking-[-0.01em] text-text">{usd(billing.amount)}</b>
                  </span>
                  <span className="min-w-0 truncate text-[12px] text-text-muted">{billing.sub}</span>
                  <span aria-hidden className="h-[30px] w-px shrink-0 bg-line" />
                  {checkChip}{allClear}
                </>
              )}
              {!billing && (<>
              <span className="flex flex-col leading-[1.15]">
                <span className="text-[11px] text-text-muted">{BASE_PRICE}</span>
                <b className="tnum text-[20px] font-bold tracking-[-0.01em] text-text">
                  {formatDollars(quote.totals.base)}
                </b>
              </span>
              {quote.totals.options > 0 && (
                <span className="tnum whitespace-nowrap text-[12px] text-text-muted">
                  {WITH_OPTIONS(formatDollars(withOptions))}
                </span>
              )}
              <span aria-hidden className="h-[30px] w-px shrink-0 bg-line" />
              <span className="flex flex-col leading-[1.15]">
                <span className="inline-flex items-center gap-1 text-[11px] text-text-muted">
                  <IconLock size={11} className="text-text-faint" /> {MARGIN_TITLE}
                </span>
                <b className="tnum text-[16px] font-bold text-text">{marginText}</b>
              </span>
              {checkChip}
              </>)}
              <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-[12.5px] font-semibold text-accent">
                {SUMMARY}
                <IconChevronUp size={14} className={cn('transition-transform', open && 'rotate-180')} />
              </span>
            </button>
            <div className="flex shrink-0 gap-2">
              <Button className="!h-10" onClick={onPreview}><IconEye size={15} /> {PREVIEW}</Button>
              {primary && (
                <Button variant="primary" className="!h-10" disabled={primary.disabled} onClick={primary.onClick}
                  data-sticky-primary="">
                  {primary.icon}{primary.label}
                </Button>
              )}
            </div>
          </div>
        )}
    </>
  )
}
